// Project Work — card row used on /project-work.
// 2026-05-28 — AFB-0006: AM badge from facilities.account_manager_eid join.
//   AFB-0007: Profit + Margin % with stoplight color tied to margin_status.
// Note: margin_pct is stored as a percentage-form number (29.95 = 29.95%) per
// trigger trg_projects_compute_margin. Do NOT multiply by 100 here.
import { Link } from 'react-router-dom';
import { User, UserPlus, AlertTriangle, Check } from 'lucide-react';
import type { ProjectRow, ProjectStage } from '../../shared/types';
import {
  PROJECT_STAGE_CLASSES,
  PROJECT_STAGE_LABELS,
} from '../../shared/types';
import { getAdvanceBlockers, isNotSent } from './lib/advance-gate';
import { subPayChip } from './lib/line-owned-payment';
import { projectDivisionLabel, projectServiceLabel } from './lib/project-service-label';
import {
  enteredClause,
  ownerCaveat,
  ownerClause,
  type AttributionRow,
  type NameLookup,
} from './lib/attribution';

interface ProjectCardProps {
  row: ProjectRow;
  // 2026-09-02 — attribution, DERIVED, batched by the list so a board of 60
  // cards costs two queries and not 120. Optional: without it the card falls
  // back to the facility's CURRENT account manager, which is what it showed
  // before and is right often enough to be a safe default.
  attribution?: AttributionRow | null;
  nameFor?: NameLookup;
  // Item #680 — whether this reader can see payment records at all. Without it
  // the sub-pay chip must not claim a line is unpaid (lib/line-owned-payment.ts).
  canSeePayments?: boolean;
}

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// margin_pct is stored as percentage form (29.95 = 29.95%) — render as-is.
function fmtMarginPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${Number(n).toFixed(1)}%`;
}

// Stoplight color class for the margin pct text. Drives off margin_status,
// which the DB trigger sets to Green / Amber / Red against the 26.5% target.
// "Internal Cost" — manual override — renders neutral with no percentage.
function marginColorClass(status: string | null | undefined): string {
  switch (status) {
    case 'Green':         return 'text-rag-green';
    case 'Amber':         return 'text-st-warn-fg';
    case 'Red':           return 'text-rag-red';
    case 'Internal Cost': return 'text-tcs-text-muted';
    default:              return 'text-tcs-text-muted';
  }
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}
function daysOverdue(due: string | null | undefined): number | null {
  if (!due) return null;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
}
function daysSince(start: string | null | undefined): number | null {
  if (!start) return null;
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : null;
}

export function ProjectCard({ row, attribution, nameFor, canSeePayments = false }: ProjectCardProps) {
  const facilityLabel = row.facilities?.facility_name ?? row.facility_name ?? row.facility_id ?? '—';
  const accountLabel = row.accounts?.account_name ?? row.account_name ?? row.account_id ?? '—';
  const stage = row.stage as ProjectStage | null;
  const stageClass = stage && PROJECT_STAGE_CLASSES[stage] ? PROJECT_STAGE_CLASSES[stage] : null;
  const stageLabel = stage && PROJECT_STAGE_LABELS[stage] ? PROJECT_STAGE_LABELS[stage] : stage;
  // 2026-08-20: these two counters used to be gated on `status === 'Active'`,
  // which meant "created in the app" rather than "still open" — so they never
  // fired on any imported row. `stage` is what actually says whether work is
  // still live, so the gate now asks the question it always meant to ask.
  const stillOpen = stage != null && stage !== 'Invoiced' && stage !== 'Completed';
  const overdueDays = stillOpen ? daysOverdue(row.due_date) : null;
  const sinceDays = stillOpen && overdueDays == null ? daysSince(row.start_date) : null;

  // AFB-0006 — AM badge state. Three possibilities, in priority order:
  //   1. No facility on the project → red warning ("data hygiene gap")
  //   2. Facility exists but account_manager_eid is null → amber warning
  //   3. Facility + EID resolved to an employee → name badge
  // Never read facilities.account_manager_name directly — it can drift from EID.
  const am = row.facilities?.account_manager;
  const amFullName = am
    ? `${am.employee_name_first ?? ''} ${am.employee_name_last ?? ''}`.trim()
    : null;
  const hasFacilityLink = !!row.facility_id;

  // 2026-09-02 — WHOSE IT IS, at the time the work was done, not now. 109 of 477
  // projects sit on a building that has changed hands since; showing today's
  // manager on those credits the wrong person's account. `ownerClause` resolves
  // out of v_project_attribution, which walks audit_log backwards.
  // ⚠️ `ownerCaveat` is the tooltip that admits when that name is the earliest
  // value on file rather than a record — the trail only begins 2026-04-29.
  const ownerText =
    attribution && nameFor ? ownerClause(attribution, nameFor) : null;
  const ownerTitle = attribution ? ownerCaveat(attribution) : null;

  let amBadge: { variant: 'name' | 'no-am' | 'no-facility'; text: string };
  if (!hasFacilityLink) {
    amBadge = { variant: 'no-facility', text: 'No facility linked' };
  } else if (ownerText) {
    amBadge = { variant: 'name', text: ownerText };
  } else if (!amFullName) {
    amBadge = { variant: 'no-am', text: 'No AM assigned' };
  } else {
    amBadge = { variant: 'name', text: amFullName };
  }

  // AFB-0007 — margin display. Three cases:
  //   1. Internal Cost (manual override) → label, no pct
  //   2. Margin present → currency profit + colored pct
  //   3. Otherwise → em-dash with tooltip
  const isInternalCost = row.margin_status === 'Internal Cost';
  const subPayChipText = subPayChip(row, canSeePayments);
  const hasMargin = row.margin_amount != null && row.margin_pct != null;
  const marginColor = marginColorClass(row.margin_status);

  // AFB-0010 — WHO TYPED IT. 2026-09-02: this used to read row.creator directly
  // and print "Submitted by <name>" on all 345 script-loaded rows, which is a
  // living employee's name on a record a load wrote (⛔ TCSD-0150). It now comes
  // from the attribution row, which returns NO PERSON for a script row and names
  // the load instead. Falls back to the old behaviour only when the batch has
  // not arrived.
  //
  // ⛔ There is deliberately NO fallback to row.creator here. `ProjectRow` does
  // not carry `source`, so a fallback could not tell a script row from a typed
  // one and would print a person's name on 345 imported records for as long as
  // the batch takes to land. A badge that appears a moment late is better than
  // one that is briefly wrong about who did the work.
  const enteredText = attribution && nameFor ? enteredClause(attribution, nameFor) : '';

  // AFB-0009 — inline bounce-back reason when the card is awaiting AM input.
  const awaitingReason =
    stage === 'Awaiting_AM_Input' ? (row.awaiting_am_input_reason?.trim() ?? '') : '';

  // AFB-0022 #5 — surface *why* a Draft can't advance to Ready to Invoice (or
  // that it's ready). Mirrors the exact gate in project-detail.tsx via the
  // shared advance-gate helper, so the chip and the advance button can't drift.
  // ⛔ BOTH not-sent stages, not just Draft. A bounced-back card is exactly the
  // one an AM most needs the badge on, and it used to be the one that lost it.
  const notSent = isNotSent(stage);
  const draftBlockers = notSent ? getAdvanceBlockers(row) : [];
  // Derived, not a stored string: a project can carry several service lines.
  const serviceLabel = projectServiceLabel(row);
  const divisionLabel = projectDivisionLabel(row);

  return (
    <li>
      <Link to={`/project-work/${row.project_id}`} className="block rounded-md border border-tcs-border bg-tcs-bg px-3 py-3 transition hover:border-tcs-text/40">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-sm font-medium text-tcs-text">
              <span className="font-mono text-xs text-tcs-text-muted">{row.project_id}</span>
              <span className="truncate">{facilityLabel}</span>
            </div>
            <div className="mt-1 truncate text-xs text-tcs-text-secondary">
              {accountLabel}{serviceLabel ? ` · ${serviceLabel}` : ''}{divisionLabel ? ` · ${divisionLabel}` : ''}
            </div>
            {/* AFB-0006 — AM badge row */}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {amBadge.variant === 'name' && (
                <span
                  className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-1.5 py-[2px] text-[11px] font-medium text-tcs-text-secondary"
                  // ⚠️ Says so when the name is the earliest value on file rather
                  // than a recorded fact. Null — and so no tooltip — whenever the
                  // audit trail actually covers this date.
                  title={ownerTitle ?? undefined}
                >
                  <User className="h-3 w-3" aria-hidden="true" />
                  {amBadge.text}
                </span>
              )}
              {amBadge.variant === 'no-am' && (
                <span
                  className="inline-flex items-center gap-1 rounded-md border border-rag-amber/40 bg-st-warn-bg px-1.5 py-[2px] text-[11px] font-medium text-st-warn-fg"
                  title="No account manager is assigned to this facility. Update the facility record to clear this warning."
                >
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  {amBadge.text}
                </span>
              )}
              {amBadge.variant === 'no-facility' && (
                <span
                  className="inline-flex items-center gap-1 rounded-md border border-rag-red/40 bg-rag-red/10 px-1.5 py-[2px] text-[11px] font-medium text-rag-red"
                  title="This project has no facility linked. Edit the project to assign one."
                >
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  {amBadge.text}
                </span>
              )}
              {/* AFB-0010 — who typed it, beside whose account it is. Two facts,
                  two badges: "Entered by Jack" next to "Melanie's account" reads
                  correctly and is honest. One field cannot carry both, and
                  carrying only the first made Jack look like the owner of 157
                  records on other managers' buildings. */}
              {enteredText && (
                <span
                  className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-1.5 py-[2px] text-[11px] font-medium text-tcs-text-secondary"
                  title="Who typed this record. A record loaded by an import names the load, never a person."
                >
                  <UserPlus className="h-3 w-3" aria-hidden="true" />
                  {enteredText}
                </span>
              )}
              {/* ⛔ The "Awaiting schedule" badge is gone with the checkbox that
                  set it — Ben, 2026-09-03. `awaiting_schedule` stays as a column;
                  nothing writes it from the UI any more, so nothing new will ever
                  wear this badge. */}
              {/* AFB-0022 #5 — tell the AM what's blocking a Draft from going to
                  Ready to Invoice (or that it's ready), so stuck drafts are
                  self-explanatory on the board. Gate mirrors project-detail.tsx. */}
              {notSent &&
                (draftBlockers.length > 0 ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-md border border-rag-amber/40 bg-st-warn-bg px-1.5 py-[2px] text-[11px] font-medium text-st-warn-fg"
                    title="This has not reached Finance yet. These are the answers still missing."
                  >
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    NOT SENT — {draftBlockers.join(' · ')}
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 rounded-md border border-rag-green/40 bg-rag-green/10 px-1.5 py-[2px] text-[11px] font-medium text-rag-green"
                    title="Everything Finance needs is answered — open it and send it."
                  >
                    <Check className="h-3 w-3" aria-hidden="true" />
                    Ready to send
                  </span>
                ))}
            </div>
            {/* AFB-0009 — bounce-back reason surfaced inline for the AM. */}
            {stage === 'Awaiting_AM_Input' && (
              <div className="mt-1.5 rounded-md border border-st-warn-border bg-st-warn-bg px-2 py-1 text-[11px] text-st-warn-fg-strong">
                <span className="font-semibold">Awaiting your input:</span>{' '}
                {awaitingReason || 'Finance requested missing details on this card.'}
              </div>
            )}
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-tcs-text-muted">
              <span>{fmtDate(row.start_date)} – {fmtDate(row.completion_date)}</span>
              <span className="text-tcs-border-strong">|</span>
              <span>Bill to customer {fmtCurrency(row.bill_amount)}</span>
              <span className="text-tcs-border-strong">|</span>
              <span>Pay to sub {fmtCurrency(row.pay_amount)}</span>
              {/* Item #680 — each service line pays its own sub, so the chip
                  reads the lines (lib/line-owned-payment.ts holds the words and
                  the rule, the same rule as My Work's alert). */}
              {subPayChipText && (
                <span className="inline-flex items-center rounded-md bg-st-warn-bg-strong px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-st-warn-fg-strong">
                  {subPayChipText}
                </span>
              )}
              <span className="text-tcs-border-strong">|</span>
              {/* AFB-0007 — Profit ($) and Margin (%) with stoplight color */}
              {isInternalCost ? (
                <span className="text-tcs-text-muted" title="Marked as internal cost — margin not applicable">
                  Internal Cost
                </span>
              ) : hasMargin ? (
                <>
                  <span>Profit {fmtCurrency(row.margin_amount)}</span>
                  <span className="text-tcs-border-strong">|</span>
                  <span className={`font-semibold ${marginColor}`}>
                    Margin {fmtMarginPct(row.margin_pct)}
                  </span>
                </>
              ) : (
                <span title="Add bill and pay amounts to see margin">Margin —</span>
              )}
              {overdueDays != null && (<><span className="text-tcs-border-strong">|</span><span className="font-medium text-rag-red">{overdueDays}d overdue</span></>)}
              {sinceDays != null && sinceDays > 0 && (<><span className="text-tcs-border-strong">|</span><span>{sinceDays}d in</span></>)}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            {stageClass && stageLabel && (
              <span className={`inline-flex items-center rounded-md px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide ${stageClass}`}>
                {stageLabel}
              </span>
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}
